// contourTracer.js — trace the equipotential contour at a clicked point.
//
// The escape field `rawIndex` (Uint32Array, row*width+col) holds an integer
// escape value per pixel (0 = interior/inside the set). An "equipotential" at
// level N is the border between {rawIndex >= N} (closer to the set) and the rest.
// We trace that border as an ORDERED path of points using square tracing (a
// Moore-style boundary follower on the binary field `inside = rawIndex >= N`).
//
// Returns { points: [{x,y}...], closed: bool, level: N } or null.
// - closed: the trace returned to its start (a loop — full/wide view).
// - open:   the trace ran into the image frame (a clipped arc — zoomed view).
//           Open arcs are traced BIDIRECTIONALLY from the seed so the whole
//           visible arc (frame edge -> seed -> frame edge) is captured, not just
//           the half on one side of the click.
function inLevel(rawIndex, width, height, x, y, N) {
  if (x < 0 || y < 0 || x >= width || y >= height) return false; // outside frame = not in level
  return rawIndex[y * width + x] >= N;
}
// Find a boundary starting cell near (sx,sy): step outward until we're just
// inside the level with an out-of-level neighbor (a border pixel).
function findStart(rawIndex, width, height, sx, sy, N) {
  const maxR = Math.max(width, height);
  for (let r = 0; r < maxR; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue; // ring only
        const x = sx + dx, y = sy + dy;
        if (!inLevel(rawIndex, width, height, x, y, N)) continue;
        if (!inLevel(rawIndex, width, height, x - 1, y, N) ||
            !inLevel(rawIndex, width, height, x + 1, y, N) ||
            !inLevel(rawIndex, width, height, x, y - 1, N) ||
            !inLevel(rawIndex, width, height, x, y + 1, N)) {
          return { x, y };
        }
      }
    }
  }
  return null;
}
// Signed area (shoelace) of a closed polygon in IMAGE coordinates (y down).
// Its sign tells us the on-screen winding, so we can orient direction
// DETERMINISTICALLY (the raw trace can come out either way depending on click).
function signedAreaImage(pts) {
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i], q = pts[(i + 1) % pts.length];
    a += p.x * q.y - q.x * p.y;
  }
  return a / 2;
}
// Re-anchor a traced path so it begins at its LEFTMOST point (canonical start),
// making phrases repeatable regardless of where the user clicked, and cleanly
// framing the x-axis symmetry of full-set loops. Closed loops rotate (a cycle
// can start anywhere) and are oriented clockwise-on-screen; open arcs orient to
// start at their leftmost endpoint.
function reanchorLeftmost(points, closed) {
  if (!points || points.length < 2) return points;
  if (closed) {
    let pts = points.slice();
    // Screen-clockwise corresponds to a negative image signed area; normalize.
    if (signedAreaImage(pts) < 0) pts = pts.reverse();
    let li = 0;
    for (let i = 1; i < pts.length; i++) {
      if (pts[i].x < pts[li].x ||
          (pts[i].x === pts[li].x && pts[i].y < pts[li].y)) li = i;
    }
    return pts.slice(li).concat(pts.slice(0, li));
  } else {
    // Open arc: start at whichever endpoint is further left.
    const first = points[0], last = points[points.length - 1];
    if (last.x < first.x) return points.slice().reverse();
    return points;
  }
}

export function traceContour(rawIndex, width, height, sx, sy) {
  sx = Math.round(sx); sy = Math.round(sy);
  if (sx < 0 || sy < 0 || sx >= width || sy >= height) return null;
  const N = rawIndex[sy * width + sx];
  if (!N || N <= 0) return null; // clicked inside the set / interior — no contour
  const start = findStart(rawIndex, width, height, sx, sy, N);
  if (!start) return null;

  const isIn = (x, y) => inLevel(rawIndex, width, height, x, y, N);
  // 8-neighborhood offsets, clockwise from East.
  const nb = [
    [1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1],
  ];
  const maxSteps = width * height * 4; // safety cap
  const onFrame = (x, y) => (x <= 0 || y <= 0 || x >= width - 1 || y >= height - 1);

  // Walk the boundary in ONE direction from the seed. `spin` (+1 clockwise,
  // -1 counter-clockwise) sets which way round the curve we go. Stops on
  // returning to the seed (closed loop) or reaching the frame (open-arc end).
  function walkFrom(seedX, seedY, firstBackDir, spin) {
    const pts = [];
    let cx = seedX, cy = seedY, backDir = firstBackDir, steps = 0, firstMove = true;
    let hitFrame = false, closedLoop = false;
    do {
      pts.push({ x: cx, y: cy });
      if (onFrame(cx, cy) && !firstMove) { hitFrame = true; break; }
      let found = false;
      for (let k = 1; k <= 8; k++) {
        const dir = (backDir + spin * k + 8) % 8;
        const nx = cx + nb[dir][0], ny = cy + nb[dir][1];
        if (isIn(nx, ny)) { backDir = (dir + 4) % 8; cx = nx; cy = ny; found = true; break; }
      }
      if (!found) break; // isolated pixel
      steps++;
      if (steps > maxSteps) break;
      if (!firstMove && cx === seedX && cy === seedY) { closedLoop = true; break; }
      firstMove = false;
    } while (true);
    return { pts, hitFrame, closedLoop };
  }

  // One OUTSIDE backtrack direction, shared by both walks. They leave the seed
  // opposite ways because they scan the neighbour ring in opposite rotations.
  let backA = 0;
  for (let i = 0; i < 8; i++) {
    if (!isIn(start.x + nb[i][0], start.y + nb[i][1])) { backA = i; break; }
  }

  const w1 = walkFrom(start.x, start.y, backA, 1);
  let points, isClosed;
  if (!w1.hitFrame) {
    // Closed loop — the walk came all the way around without reaching the frame.
    points = w1.pts;
    isClosed = true;
  } else {
    // Open arc — the seed was mid-arc. Walk the OTHER way to the other frame
    // edge, then stitch into a continuous edge -> seed -> edge path:
    // reverse(w2 minus seed) + w1  =  edgeB -> seed -> edgeA.
    const w2 = walkFrom(start.x, start.y, backA, -1);
    points = w2.pts.slice(1).reverse().concat(w1.pts);
    isClosed = false;
  }
  const anchored = reanchorLeftmost(points, isClosed);
  return { points: anchored, closed: isClosed, level: N };
}
